<script lang="ts">
  // Approved & Queued tab per INTERFACE-SPECIFICATION.md §8.
  //
  // Lists every manifest version with status === 'approved' (future-
  // dated). These are changes whose PR has merged but whose
  // effective-from instant has not yet arrived. Sorted by
  // effective-from (soonest first).
  //
  // PR 3.2.3 adds two reviewer-only actions per card:
  //   [Publish now] — fast-forward to in_effect immediately
  //   [De-queue]    — cancel the queued change before it goes live
  // Both require a justification logged on the audit record. The role
  // check is `reviewer:{value_type}` for the entry's value_type, or
  // admin as an explicit escape hatch.

  import { onMount } from 'svelte';
  import { catalogueState, bootstrapManifest, cachedCatalogue, fetchCatalogue } from '../lib/catalogue-store.svelte';
  import { getPredecessorInEffectAt } from '../lib/manifest-helpers';
  import { identity } from '../lib/identity.svelte';
  import { PRODUCTS, findProduct } from '../lib/products';
  import { formatLocalDate, formatEffectiveTimestamp, formatClampedFromNote } from '../lib/formatters';
  import { api, ApiError } from '../lib/api';
  import { navigate } from '../lib/router.svelte';
  import { adoptDraftFromServer } from '../lib/staged-edits.svelte';
  import type { ManifestVersionEntry, StaleQueuedIntermediate, Jurisdiction, ProductId } from '../lib/types';

  interface Row {
    product_label: string;
    jurisdiction: Jurisdiction;
    product: ProductId;
    version: ManifestVersionEntry;
  }

  let actionError = $state<string | null>(null);
  let actionInFlight = $state<string | null>(null); // catalogue_version of the entry being acted on

  // INTERFACE-SPECIFICATION.md §8.1 staleness banner state. Per
  // queued entry, the loader fetches the intermediate manifest
  // entries that have gone live since the queued was approved. An
  // empty array (or absent key) means the entry is fresh; a
  // non-empty array means the amber banner renders. expanded tracks
  // the entries the operator has clicked "Review against current
  // state" on so the per-cell diff appears inline.
  const staleness = $state<Record<string, StaleQueuedIntermediate[]>>({});
  const stalenessLoading = $state<Record<string, boolean>>({});
  const expanded = $state<Record<string, boolean>>({});

  const rows = $derived(buildRows());

  onMount(async () => {
    if (!catalogueState.manifest) await bootstrapManifest();
  });

  // Whenever the rendered rows change, fetch staleness for any new
  // queued entries we have not checked yet. Per-entry detection is
  // cheap (the production manifest carries at most a handful of
  // queued entries at any time) and keeps each card's banner
  // independent of the others.
  $effect(() => {
    for (const row of rows) {
      const cv = row.version.catalogue_version;
      if (staleness[cv] !== undefined || stalenessLoading[cv]) continue;
      stalenessLoading[cv] = true;
      api.staleQueuedEntry(cv)
        .then((response) => {
          staleness[cv] = response.intermediate;
        })
        .catch(() => {
          // Treat a failed staleness check as "no signal" rather than
          // a hard error — the operator still sees the card and the
          // queue actions. A subsequent refresh will retry.
          staleness[cv] = [];
        })
        .finally(() => {
          stalenessLoading[cv] = false;
        });
    }
  });

  function toggleExpanded(cv: string): void {
    expanded[cv] = !expanded[cv];
  }

  function buildRows(): Row[] {
    if (!catalogueState.manifest) return [];
    const out: Row[] = [];
    for (const p of PRODUCTS) {
      const versions = catalogueState.manifest.jurisdictions?.[p.jurisdiction]?.[p.product]?.versions ?? [];
      for (const v of versions) {
        if (v.status === 'approved') {
          out.push({
            product_label: p.label,
            jurisdiction: p.jurisdiction,
            product: p.product,
            version: v
          });
        }
      }
    }
    out.sort((a, b) => (a.version.effective_from.utc < b.version.effective_from.utc ? -1 : 1));
    return out;
  }

  // Hide-not-disable: the buttons render only for users who hold
  // reviewer:{value_type} for the entry's value_type, or admin. The
  // server enforces the same gate; this is the UX optimisation.
  function canActOn(version: ManifestVersionEntry): boolean {
    if (identity.roles.includes('admin')) return true;
    const valueType = version.value_type || 'rates';
    return identity.roles.includes(`reviewer:${valueType}`);
  }

  // Build the History comparison link for a queued entry. The link
  // pre-selects (predecessor git_hash, queued git_hash) so the
  // reviewer sees the same diff view the History tab renders for a
  // manually-ticked pair. The predecessor is the most recent entry
  // (non-dequeued) whose effective_from precedes the queued entry's
  // effective_from — the snapshot a customer would receive the moment
  // before the queued entry's time gate fires. In the simple two-
  // version case (in-effect plus one queued) this resolves to the
  // current in-effect entry; in the three-queued case (A queued before
  // B, D queued after B) it correctly resolves to B's predecessor (A
  // or the in-effect, whichever is later), not the live in-effect that
  // already carries D's edits B's snapshot never saw. Returns null
  // when no predecessor exists at all (rare; bootstrap is the floor).
  function compareHref(row: Row): string | null {
    const predecessor = getPredecessorInEffectAt(
      catalogueState.manifest,
      row.jurisdiction,
      row.product,
      row.version.effective_from.utc
    );
    if (!predecessor) return null;
    const compare = `${predecessor.git_hash},${row.version.git_hash}`;
    return `/history/${row.jurisdiction}/${row.product}?compare=${encodeURIComponent(compare)}`;
  }

  // Re-author needs proposer (not reviewer) — the new draft lives in
  // the caller's namespace and goes through the standard propose flow.
  // The server enforces proposer:{value_type} inside saveDraft.
  function canReauthor(version: ManifestVersionEntry): boolean {
    if (identity.roles.includes('admin')) return true;
    const valueType = version.value_type || 'rates';
    return identity.roles.includes(`proposer:${valueType}`);
  }

  async function handleReauthor(row: Row): Promise<void> {
    if (typeof window === 'undefined') return;
    const confirmed = window.confirm(
      `Re-author "${row.version.catalogue_version}" from current main?\n\n` +
      `A new draft will be created on your branch, rooted at current main, ` +
      `pre-filled with this queued entry's edits. When the new proposal is ` +
      `approved, the original queued entry will be automatically de-queued.\n\n` +
      `You will be taken to the new draft to review and submit.`
    );
    if (!confirmed) return;
    actionError = null;
    actionInFlight = row.version.catalogue_version;
    try {
      const created = await api.reauthorQueuedEntry(row.version.catalogue_version);
      // Hydrate the staged-edits store from the server-side draft so the
      // editor renders the seeded edits when the operator lands. Without
      // this step the CatalogueDetail mount runs loadFromSession, finds
      // no sessionStorage entry for the new slug, and starts a fresh
      // empty draft — leaving the operator unable to see what was
      // re-applied. We also ensure the catalogue is loaded so the
      // before values are looked up against main's cube.
      const catalogue = cachedCatalogue(created.jurisdiction, created.product)
        ?? await fetchCatalogue(created.jurisdiction, created.product);
      if (catalogue) {
        await adoptDraftFromServer(created.draft_slug, catalogue);
      }
      navigate(`/catalogue/detail/${created.jurisdiction}/${created.product}`);
    } catch (err) {
      actionError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 're_author_failed';
    } finally {
      actionInFlight = null;
    }
  }

  async function handlePublishNow(version: ManifestVersionEntry) {
    if (typeof window === 'undefined') return;
    const confirmed = window.confirm(
      `Publish "${version.catalogue_version}" immediately?\n\n` +
      `This change is currently scheduled for ${formatEffectiveTimestamp(version.effective_from)}. ` +
      `Publishing now will fire the time-gate immediately — customers will see the new cube within seconds. ` +
      `The previous in-effect version will be superseded.`
    );
    if (!confirmed) return;
    const justification = window.prompt(
      'Why are you publishing this now? (required — logged on the audit record)',
      ''
    );
    if (!justification || justification.trim().length === 0) {
      actionError = 'Publish-now cancelled — a justification is required.';
      return;
    }
    actionError = null;
    actionInFlight = version.catalogue_version;
    try {
      await api.publishNowManifestEntry(version.catalogue_version, justification.trim());
      await bootstrapManifest();
    } catch (err) {
      actionError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'publish_now_failed';
    } finally {
      actionInFlight = null;
    }
  }

  async function handleDequeue(version: ManifestVersionEntry) {
    if (typeof window === 'undefined') return;
    const confirmed = window.confirm(
      `De-queue "${version.catalogue_version}"?\n\n` +
      `This change is currently scheduled for ${formatEffectiveTimestamp(version.effective_from)}. ` +
      `De-queuing cancels it — it will not become live. The previous in-effect version stays in place. ` +
      `Per spec design principle 1.4, undoing an already-live change requires a new rollback proposal; ` +
      `this action is only for changes that have not yet been served to anyone.`
    );
    if (!confirmed) return;
    const justification = window.prompt(
      'Why are you de-queuing this? (required — logged on the audit record)',
      ''
    );
    if (!justification || justification.trim().length === 0) {
      actionError = 'De-queue cancelled — a justification is required.';
      return;
    }
    actionError = null;
    actionInFlight = version.catalogue_version;
    try {
      await api.dequeueManifestEntry(version.catalogue_version, justification.trim());
      await bootstrapManifest();
    } catch (err) {
      actionError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error ? err.message : 'dequeue_failed';
    } finally {
      actionInFlight = null;
    }
  }
</script>

<section>
  {#if catalogueState.loading}
    <p class="muted">Loading manifest…</p>
  {:else if rows.length === 0}
    <p class="muted">No approved changes queued for activation.</p>
  {:else}
    {#if actionError}
      <div class="banner banner-fail">{actionError}</div>
    {/if}
    <ul class="queued-list">
      {#each rows as row (row.product_label + row.version.catalogue_version)}
        {@const stale = staleness[row.version.catalogue_version] ?? []}
        {@const compareLink = compareHref(row)}
        <li class="queued-card">
          <header>
            <strong>{row.product_label}</strong>
            <span class="muted">{row.version.catalogue_version}</span>
            <span class="badge badge-info">Approved · queued</span>
          </header>
          <p>{row.version.change_summary ?? ''}</p>
          <dl>
            <div>
              <dt>Effective from</dt>
              <dd>
                {formatEffectiveTimestamp(row.version.effective_from)}
                {#if row.version.proposed_effective_from}
                  <div class="clamp-note muted">{formatClampedFromNote(row.version.proposed_effective_from)}</div>
                {/if}
              </dd>
            </div>
            <div><dt>Approved by</dt><dd>{row.version.approved_by ?? '—'}</dd></div>
            <div><dt>Proposed by</dt><dd>{row.version.proposed_by ?? '—'}</dd></div>
            <div><dt>Catalogue hash</dt><dd><code>{row.version.git_hash}</code></dd></div>
          </dl>
          {#if compareLink}
            <!-- Read-only deep link into the History tab's comparison
                 view, pre-selecting (current in-effect ↔ this queued
                 entry). Lets a reviewer see the full cell-by-cell
                 diff before deciding to Publish-now, Re-author, or
                 De-queue. Visible to anyone — the comparison is a
                 read action; the action buttons stay role-gated. -->
            <p class="queued-card__compare">
              <a href={compareLink}>Compare against current in-effect →</a>
            </p>
          {/if}
          {#if stale.length > 0}
            <!-- INTERFACE-SPECIFICATION.md §8.1 staleness banner.
                 Intermediate manifest entries have gone live on the
                 same (jurisdiction, product) since this queued entry
                 was approved. When this entry's time-gate fires, its
                 frozen snapshot will overwrite the intermediate edits. -->
            <aside class="staleness-banner" role="alert">
              <p class="staleness-banner__heading">
                <span aria-hidden="true">⚠</span>
                Stale: {stale.length} change{stale.length === 1 ? '' : 's'} since approval
              </p>
              <p>
                Since this change was approved,
                {stale.length}
                change{stale.length === 1 ? '' : 's'} {stale.length === 1 ? 'has' : 'have'}
                gone live on {row.product_label}
                ({stale.map(s => s.catalogue_version).join(', ')}).
                The queued snapshot does not include those edits and will overwrite them when it activates.
              </p>
              <div class="staleness-banner__actions">
                <button
                  class="btn-link"
                  type="button"
                  onclick={() => toggleExpanded(row.version.catalogue_version)}
                >
                  {expanded[row.version.catalogue_version] ? 'Hide diff' : 'Review against current state'}
                </button>
                {#if canReauthor(row.version)}
                  <button
                    class="btn-link"
                    type="button"
                    disabled={actionInFlight !== null}
                    onclick={() => handleReauthor(row)}
                  >
                    {actionInFlight === row.version.catalogue_version ? 'Re-authoring…' : 'Re-author from current →'}
                  </button>
                {/if}
              </div>
              {#if expanded[row.version.catalogue_version]}
                <ul class="staleness-detail">
                  {#each stale as inter (inter.catalogue_version)}
                    <li>
                      <p class="staleness-detail__title">
                        <code>{inter.catalogue_version}</code>
                        <span class="muted">
                          · effective {formatEffectiveTimestamp(inter.effective_from)}
                          {#if inter.approved_by}
                            · approved by {inter.approved_by}
                          {/if}
                        </span>
                      </p>
                      {#if inter.change_summary}
                        <p class="staleness-detail__summary"><em>{inter.change_summary}</em></p>
                      {/if}
                      {#if inter.diverging_count === 0}
                        <p class="muted">No cell-level divergence — the queued snapshot already matches this entry.</p>
                      {:else}
                        <p class="muted">
                          {inter.diverging_count} cell{inter.diverging_count === 1 ? '' : 's'} will revert when this queued change activates:
                        </p>
                        <ul class="staleness-cells">
                          {#each inter.diverging_cells.cube as cell}
                            <li>
                              <code>{cell.coordinates}</code>
                              <span class="muted"> · {JSON.stringify(cell.previous)} → {JSON.stringify(cell.next)}</span>
                            </li>
                          {/each}
                          {#each inter.diverging_cells.fee as fee}
                            <li>
                              <code>{fee.path}</code>
                              <span class="muted"> · will revert to {JSON.stringify(fee.new_value)}</span>
                            </li>
                          {/each}
                          {#each inter.diverging_cells.fee_override as override}
                            <li>
                              <code>rate_cube.{override.credit_rating_id}.establishment_fee_override</code>
                              {#if override.new_value === null}
                                <span class="muted"> · the queued snapshot has no override and will remove the intermediate's</span>
                              {:else}
                                <span class="muted"> · will revert to {JSON.stringify(override.new_value.tiers)}</span>
                              {/if}
                            </li>
                          {/each}
                          {#each inter.diverging_cells.amount as amount}
                            <li>
                              <code>loan_amount_range.{amount.field}</code>
                              <span class="muted"> · will revert to {amount.new_value}</span>
                            </li>
                          {/each}
                          {#each inter.diverging_cells.purpose as purpose}
                            <li>
                              {#if purpose.action === 'remove'}
                                <code>loan_purposes.{purpose.purpose_id}</code>
                                <span class="muted"> · will be added back when the queued entry activates</span>
                              {:else if purpose.action === 'add'}
                                <span class="muted">A purpose added by this intermediate will be removed when the queued entry activates</span>
                              {:else}
                                <code>loan_purposes.{purpose.purpose_id}</code>
                                <span class="muted"> · the queued snapshot has different fields</span>
                              {/if}
                            </li>
                          {/each}
                          {#if inter.diverging_cells.term.length > 0}
                            <li>
                              <code>terms_months</code>
                              <span class="muted"> · the queued snapshot has a different list ({inter.diverging_cells.term.join(', ')})</span>
                            </li>
                          {/if}
                        </ul>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {/if}
            </aside>
          {/if}
          {#if canActOn(row.version)}
            <div class="queued-card__actions">
              <button
                class="btn btn-primary"
                type="button"
                onclick={() => handlePublishNow(row.version)}
                disabled={actionInFlight !== null}
              >
                {actionInFlight === row.version.catalogue_version ? 'Working…' : 'Publish now →'}
              </button>
              <button
                class="btn"
                type="button"
                onclick={() => handleDequeue(row.version)}
                disabled={actionInFlight !== null}
              >
                De-queue
              </button>
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .queued-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }
  .queued-card {
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-4);
  }
  .queued-card header { display: flex; gap: var(--space-3); align-items: baseline; flex-wrap: wrap; margin-bottom: var(--space-2); }
  .queued-card dl { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--space-2); margin: 0; }
  .queued-card dt { font-size: var(--type-sm); color: var(--surface-muted); }
  .queued-card dd { margin: 0; font-size: var(--type-sm); }
  .queued-card__actions {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-3);
    padding-top: var(--space-2);
    border-top: 1px solid var(--surface-line-soft);
  }
  .queued-card__compare {
    margin: var(--space-2) 0 0 0;
    font-size: var(--type-sm);
  }
  .queued-card__compare a {
    color: var(--surface-fg);
    text-decoration: underline;
  }

  /* §8.1 staleness banner. Amber accent, sits between the metadata
     and the action buttons so the operator sees the warning before
     clicking Publish-now. */
  .staleness-banner {
    margin-top: var(--space-3);
    padding: var(--space-3);
    border: 1px solid var(--color-warn, #b45309);
    border-left-width: 4px;
    border-radius: var(--radius-sm);
    background: var(--surface-card);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .staleness-banner__heading {
    margin: 0;
    color: var(--color-warn, #b45309);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .staleness-banner__actions {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .staleness-banner p {
    margin: 0;
    font-size: var(--type-sm);
  }
  .staleness-detail {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    border-top: 1px solid var(--surface-line-soft);
    padding-top: var(--space-2);
  }
  .staleness-detail__title {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--type-sm);
    font-weight: 600;
  }
  .staleness-detail__summary {
    margin: var(--space-1) 0 0 0;
    font-size: var(--type-sm);
  }
  .staleness-cells {
    list-style: disc;
    margin: var(--space-1) 0 0 0;
    padding-left: var(--space-4);
    font-size: var(--type-sm);
  }
  .staleness-cells code { font-family: var(--font-mono); }
  code { font-family: var(--font-mono); }
</style>
